import type { Broadcast } from '../types/broadcast';
import type { UserRole } from '../types/user';

// Stage 6A v6 — client-side delete-affordance visibility + confirm copy.
//
// The list and detail surfaces only ever show broadcasts the user can VIEW
// (admin / initiator / brand-AM — enforced server-side). Given that precondition
// the delete rule collapses to something the client can evaluate from just the
// uid + role, no accessible-brands lookup needed:
//   - a DRAFT in your list is always deletable by you (for drafts view == delete);
//   - a non-draft (it produced sends) is deletable only by an admin or its
//     initiator — a brand-AM who didn't initiate it cannot.
// The server re-enforces the full rule on DELETE, so this is purely a display
// hint (worst case the button 403s).
export function canDeleteBroadcastFromList(
  broadcast: Pick<Broadcast, 'status' | 'createdBy'>,
  uid: string,
  role: UserRole,
): boolean {
  if (broadcast.status === 'draft') return true;
  return role === 'admin' || broadcast.createdBy === uid;
}

// Confirm-dialog copy differs by delete kind, which is status-driven (draft =>
// hard/permanent; anything else => soft/hidden-but-preserved).
export function deleteConfirmCopy(status: Broadcast['status']): {
  title: string;
  message: string;
  confirmLabel: string;
} {
  if (status === 'draft') {
    return {
      title: 'Delete this draft?',
      message: 'It will be removed permanently.',
      confirmLabel: 'Delete draft',
    };
  }
  return {
    title: 'Delete this broadcast?',
    message:
      'It will be hidden from the list, but recipient data, send records, and the audit trail are preserved.',
    confirmLabel: 'Delete broadcast',
  };
}
